import {
  BoldOutlined,
  ItalicOutlined,
  StrikethroughOutlined,
  UnderlineOutlined,
} from '@ant-design/icons';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $isTableCellNode,
  $isTableSelection,
  TableSelection,
} from '@lexical/table';
import {
  $getSelection,
  $isRangeSelection,
  $isTextNode,
  FORMAT_TEXT_COMMAND,
  LexicalEditor,
  RangeSelection,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { getDOMRangeRect } from '../../rich-lexical-editor/utils/getDOMRangeRect';

const LowPriority = 1;

// Global state to track active floating toolbars
let activeToolbarInstance: string | null = null;
const toolbarInstances = new Map<string, () => void>();

function getSelectedNode(selection: RangeSelection | TableSelection) {
  const anchor = selection.anchor;
  const focus = selection.focus;
  const anchorNode = selection.anchor.getNode();
  const focusNode = selection.focus.getNode();
  if (anchorNode === focusNode) {
    return anchorNode;
  }
  const isBackward = selection.isBackward();
  if (isBackward) {
    return $isTextNode(focus.getNode()) ? anchorNode : focusNode;
  } else {
    return $isTextNode(anchor.getNode()) ? focusNode : anchorNode;
  }
}

function getFontSizeFromStyle(style: string): {
  fontSize: number;
  lineHeight: number;
} {
  const fontSizeMatch = style.match(/font-size:\s*(\d+)px/);
  const lineHeightMatch = style.match(/line-height:\s*([\d.]+)/);

  return {
    fontSize: fontSizeMatch ? parseInt(fontSizeMatch[1], 10) : 14, // Default font size
    lineHeight: lineHeightMatch ? parseFloat(lineHeightMatch[1]) : 1.6, // Default line height
  };
}

function FloatingTextFormatToolbar({
  editor,
  instanceId,
}: {
  editor: LexicalEditor;
  anchorElem: HTMLElement;
  instanceId: string;
}) {
  const popupCharStylesEditorRef = useRef<HTMLDivElement | null>(null);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [isCode, setIsCode] = useState(false);
  const [fontSize, setFontSize] = useState(14);
  const [lineHeight, setLineHeight] = useState(1.2);
  const [isVisible, setIsVisible] = useState(false);
  const throttleTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastUpdateTimeRef = useRef<number>(0);

  const hideToolbar = useCallback(() => {
    const popupCharStylesEditorElem = popupCharStylesEditorRef.current;
    if (popupCharStylesEditorElem) {
      popupCharStylesEditorElem.style.opacity = '0';
      popupCharStylesEditorElem.style.transform = 'translate(0, 10px)';
      popupCharStylesEditorElem.style.pointerEvents = 'none';
    }
    setIsVisible(false);
    if (activeToolbarInstance === instanceId) {
      activeToolbarInstance = null;
    }
  }, [instanceId]);

  // Register this instance's hide function
  useEffect(() => {
    toolbarInstances.set(instanceId, hideToolbar);
    return () => {
      toolbarInstances.delete(instanceId);
      if (activeToolbarInstance === instanceId) {
        activeToolbarInstance = null;
      }
    };
  }, [instanceId, hideToolbar]);

  const updateTextFormatFloatingToolbar = useCallback(() => {
    const selection = $getSelection();
    const popupCharStylesEditorElem = popupCharStylesEditorRef.current;
    const nativeSelection = window.getSelection();

    if (popupCharStylesEditorElem === null) {
      return;
    }

    const rootElement = editor.getRootElement();

    // Check if this editor has focus
    const editorHasFocus =
      rootElement && rootElement.contains(document.activeElement);

    const isRangeSelectionVisible =
      selection !== null &&
      nativeSelection !== null &&
      !nativeSelection.isCollapsed &&
      rootElement !== null &&
      rootElement.contains(nativeSelection.anchorNode) &&
      editorHasFocus;

    const isTableSelection = $isTableSelection(selection);

    if (isRangeSelectionVisible || isTableSelection) {
      // Hide all other toolbar instances before showing this one
      if (activeToolbarInstance && activeToolbarInstance !== instanceId) {
        const hideOther = toolbarInstances.get(activeToolbarInstance);
        if (hideOther) {
          hideOther();
        }
      }

      activeToolbarInstance = instanceId;

      let rangeRect;
      if (isTableSelection) {
        const tableCellNodes = selection.getNodes();
        if (tableCellNodes.length > 0) {
          const firstCellDom = editor.getElementByKey(
            tableCellNodes[0].getKey()
          );
          if (firstCellDom) {
            rangeRect = firstCellDom.getBoundingClientRect();
          }
        }
      } else {
        try {
          if (nativeSelection && rootElement) {
            // rangeRect = nativeSelection.getRangeAt(0).getBoundingClientRect();
            rangeRect = getDOMRangeRect(nativeSelection, rootElement);
          }
        } catch (error) {
          hideToolbar();
        }
      }

      if (!rangeRect) {
        return;
      }

      const { left, top, width } = rangeRect;

      // Calculate toolbar position with boundary checking
      const toolbarWidth = 280; // minWidth from style
      const viewportWidth = window.innerWidth;
      const padding = 10; // Minimum padding from viewport edges

      let toolbarLeft = left + width / 2 - toolbarWidth / 2;

      // Ensure toolbar doesn't go off the left edge
      if (toolbarLeft < padding) {
        toolbarLeft = padding;
      }

      // Ensure toolbar doesn't go off the right edge
      if (toolbarLeft + toolbarWidth > viewportWidth - padding) {
        toolbarLeft = viewportWidth - toolbarWidth - padding;
      }

      popupCharStylesEditorElem.style.opacity = '1';
      popupCharStylesEditorElem.style.top = `${top - 50}px`;
      popupCharStylesEditorElem.style.left = `${toolbarLeft}px`;
      popupCharStylesEditorElem.style.transform = 'translate(0, 0)';
      popupCharStylesEditorElem.style.pointerEvents = 'auto';
      setIsVisible(true);

      if (!$isRangeSelection(selection) && !isTableSelection) {
        return;
      }

      // Update formatting states
      setIsBold(selection.hasFormat('bold'));
      setIsItalic(selection.hasFormat('italic'));
      setIsUnderline(selection.hasFormat('underline'));
      setIsStrikethrough(selection.hasFormat('strikethrough'));
      setIsCode(selection.hasFormat('code'));

      // Update font size
      const node = getSelectedNode(selection);
      if (node) {
        // Only call getStyle() on text nodes
        if ($isTextNode(node)) {
          const style = node.getStyle();
          const { fontSize, lineHeight } = getFontSizeFromStyle(style);
          setFontSize(fontSize);
          setLineHeight(lineHeight);
        } else {
          // For non-text nodes, try to find a text node in the selection
          const textNodes = selection.getNodes().filter($isTextNode);
          if (textNodes.length > 0) {
            const style = textNodes[0].getStyle();
            const { fontSize, lineHeight } = getFontSizeFromStyle(style);
            setFontSize(fontSize);
            setLineHeight(lineHeight);
          } else {
            setFontSize(14); // Default font size
          }
        }
      }
    } else {
      hideToolbar();
    }
  }, [editor, instanceId, hideToolbar]);

  // Throttled version of updateTextFormatFloatingToolbar
  const throttledUpdate = useCallback(() => {
    const now = Date.now();
    const timeSinceLastUpdate = now - lastUpdateTimeRef.current;
    const throttleDelay = 100; // 100ms throttle

    if (timeSinceLastUpdate >= throttleDelay) {
      lastUpdateTimeRef.current = now;
      updateTextFormatFloatingToolbar();
    } else {
      // Schedule update for later
      if (throttleTimerRef.current) {
        clearTimeout(throttleTimerRef.current);
      }
      throttleTimerRef.current = setTimeout(() => {
        lastUpdateTimeRef.current = Date.now();
        editor.getEditorState().read(() => {
          updateTextFormatFloatingToolbar();
        });
      }, throttleDelay - timeSinceLastUpdate);
    }
  }, [editor, updateTextFormatFloatingToolbar]);

  const handleFontSizeChange = useCallback(
    (delta: number) => {
      editor.update(() => {
        const selection = $getSelection();
        const newFontSize = Math.max(8, Math.min(72, fontSize + delta));

        const updateStyle = (currentStyle: string) => {
          let updatedStyle = currentStyle
            .replace(/font-size:\s*\d+px;?/g, '')
            .replace(/line-height:\s*[\d.]+;?/g, '')
            .replace(/display:\s*[^;]+;?/g, '');

          updatedStyle = updatedStyle.replace(/;+/g, ';').replace(/^;|;$/g, '');

          const styleToAdd = `font-size: ${newFontSize}px; line-height: ${lineHeight}; display: inline-block;`;
          return updatedStyle ? `${updatedStyle}; ${styleToAdd}` : styleToAdd;
        };

        if ($isRangeSelection(selection)) {
          selection.getNodes().forEach((node) => {
            if ($isTextNode(node)) {
              node.setStyle(updateStyle(node.getStyle()));
            }
          });
        } else if ($isTableSelection(selection)) {
          selection.getNodes().forEach((cellNode) => {
            if ($isTableCellNode(cellNode)) {
              cellNode.getAllTextNodes().forEach((textNode) => {
                textNode.setStyle(updateStyle(textNode.getStyle()));
              });
            }
          });
        }
        setFontSize(newFontSize);
      });
    },
    [editor, fontSize, lineHeight]
  );

  // Handle clicks outside the editor to hide toolbar
  useEffect(() => {
    const handleBlur = () => {
      if (throttleTimerRef.current) {
        clearTimeout(throttleTimerRef.current);
      }
      hideToolbar();
    };

    const handleScroll = () => {
      if (throttleTimerRef.current) {
        clearTimeout(throttleTimerRef.current);
      }
      hideToolbar();
    };

    window.addEventListener('blur', handleBlur);
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      if (throttleTimerRef.current) {
        clearTimeout(throttleTimerRef.current);
      }
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [hideToolbar]);

  useEffect(() => {
    const updateTextFormatFloatingToolbarInner = () => {
      editor.getEditorState().read(() => {
        throttledUpdate();
      });
    };

    const unregisterUpdate = editor.registerUpdateListener(
      ({ editorState }: { editorState: any }) => {
        editorState.read(() => {
          throttledUpdate();
        });
      }
    );

    const unregisterSelection = editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      () => {
        updateTextFormatFloatingToolbarInner();
        return false;
      },
      LowPriority
    );

    return () => {
      if (throttleTimerRef.current) {
        clearTimeout(throttleTimerRef.current);
      }
      unregisterUpdate();
      unregisterSelection();
    };
  }, [editor, throttledUpdate]);

  return (
    <div
      ref={popupCharStylesEditorRef}
      className="floating-text-format-popup"
      style={{
        position: 'fixed',
        zIndex: 1000,
        opacity: 0,
        background: '#fff',
        borderRadius: '8px',
        padding: '4px 4px',
        display: 'flex',
        gap: '4px',
        border: '1px solid #d9d9d9',
        top: '-1000px',
        left: '-1000px',
        minWidth: '250px',
        justifyContent: 'center',
        alignItems: 'center',
        pointerEvents: isVisible ? 'auto' : 'none', // Prevent interaction when hidden
        transition: 'opacity 0.1s ease-in-out, transform 0.2s ease-in-out',
        transform: 'translate(0, 10px)',
      }}
    >
      <button
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
        }}
        className={`floating-text-format-button ${isBold ? 'active' : ''}`}
        aria-label="Format Bold"
        type="button"
        style={{
          background: isBold ? '#1890ff' : 'transparent',
          color: isBold ? '#fff' : '#000',
        }}
      >
        <BoldOutlined />
      </button>
      <button
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
        }}
        className={`floating-text-format-button ${isItalic ? 'active' : ''}`}
        aria-label="Format Italics"
        type="button"
        style={{
          background: isItalic ? '#1890ff' : 'transparent',
          color: isItalic ? '#fff' : '#000',
        }}
      >
        <ItalicOutlined />
      </button>
      <button
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline');
        }}
        className={`floating-text-format-button ${isUnderline ? 'active' : ''}`}
        aria-label="Format Underline"
        type="button"
        style={{
          background: isUnderline ? '#1890ff' : 'transparent',
          color: isUnderline ? '#fff' : '#000',
        }}
      >
        <UnderlineOutlined />
      </button>
      <button
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough');
        }}
        className={`floating-text-format-button ${
          isStrikethrough ? 'active' : ''
        }`}
        aria-label="Format Strikethrough"
        type="button"
        style={{
          background: isStrikethrough ? '#1890ff' : 'transparent',
          color: isStrikethrough ? '#fff' : '#000',
        }}
      >
        <StrikethroughOutlined />
      </button>
      <button
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'code');
        }}
        className={`floating-text-format-button ${isCode ? 'active' : ''}`}
        aria-label="Format Code"
        type="button"
        style={{
          background: isCode ? '#1890ff' : 'transparent',
          color: isCode ? '#fff' : '#000',
        }}
      >
        &lt;/&gt;
      </button>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '2px',
          borderLeft: '1px solid #d9d9d9',
          paddingLeft: '8px',
          marginLeft: '4px',
        }}
      >
        <button
          onClick={() => handleFontSizeChange(-2)}
          className="floating-text-format-button"
          aria-label="Decrease Font Size"
          type="button"
          style={{
            color: '#000',
            lineHeight: 1,
          }}
        >
          A⁻
        </button>
        <span
          style={{
            fontSize: '14px',
            marginTop: '1px',
            minWidth: '24px',
            textAlign: 'center',
          }}
        >
          {fontSize}
        </span>
        <button
          onClick={() => handleFontSizeChange(2)}
          className="floating-text-format-button"
          aria-label="Increase Font Size"
          type="button"
          style={{
            color: '#000',
            lineHeight: 1,
          }}
        >
          A⁺
        </button>
      </div>
    </div>
  );
}

export default function FloatingTextFormatToolbarPlugin({
  anchorElem = document.body,
}: {
  anchorElem?: HTMLElement;
}): JSX.Element | null {
  const [editor] = useLexicalComposerContext();

  // Generate a unique instance ID for this toolbar
  const instanceId = useRef(Math.random().toString(36).slice(2, 11)).current;

  return createPortal(
    <FloatingTextFormatToolbar
      editor={editor}
      anchorElem={anchorElem}
      instanceId={instanceId}
    />,
    document.body
  );
}
